import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { BookOpen } from 'lucide-react';
import { GUIA_NOTAS_BKO, guiaDoTipo } from '@/lib/notas/guiaNotasBko';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Abre já no cargo da pessoa. Sem isso, mostra todos. */
  tipo?: string | null;
}

/**
 * Guia das notas do Backoffice — o que conta, quanto vale e como melhorar.
 *
 * Douglas (21/08/2026): "crie um guia de notas e pilar de análise do BKO."
 * Fica ao lado da nota, não numa página escondida: quem abre a própria nota
 * consegue ler ali mesmo por que ela é aquela.
 */
export function GuiaNotasDialog({ open, onOpenChange, tipo }: Props) {
  const soUm = guiaDoTipo(tipo);
  const reguas = soUm ? [soUm] : GUIA_NOTAS_BKO;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-primary" />
            Guia da nota{soUm ? ` — ${soUm.nome}` : 's do Backoffice'}
          </DialogTitle>
          <DialogDescription className="text-xs leading-relaxed">
            Dez pilares por cargo, cem pontos, apurados no mês corrente. Todo pilar é algo que
            dá para mexer até o último dia do mês — por isso são fila de agora, taxa do mês e
            velocidade, nunca foto de um dia que já passou.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-7">
          {reguas.map((r) => (
            <section key={r.tipo} className="space-y-3">
              {!soUm && (
                <h3 className="font-semibold text-base flex items-center gap-2">
                  {r.nome}
                  <Badge variant="outline" className="text-[10px] font-normal">{r.tipo}</Badge>
                </h3>
              )}
              <p className="text-[13px] text-muted-foreground leading-relaxed">{r.escopo}</p>

              {r.blocos.map((b) => (
                <div key={b.titulo} className="space-y-2">
                  <div className="font-mono text-[10px] uppercase tracking-[0.09em] text-muted-foreground border-t pt-3">
                    {b.titulo}
                  </div>
                  {b.pilares.map((p) => (
                    <div key={p.pilar} className="rounded-lg border bg-muted/20 p-3">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-medium text-sm flex-1 min-w-[180px]">{p.pilar}</span>
                        <Badge className="bg-primary/10 text-primary border-primary/30 rounded-full text-[11px] font-normal">
                          {p.peso} pts
                        </Badge>
                        <Badge variant="outline" className="text-[11px] font-normal">
                          meta {p.meta}
                        </Badge>
                      </div>
                      <p className="text-[13px] text-muted-foreground leading-relaxed mt-1.5">{p.mede}</p>
                      <p className="text-[12px] leading-relaxed mt-1.5 border-l-2 border-primary/30 pl-2.5">
                        {p.comoMelhorar}
                      </p>
                    </div>
                  ))}
                </div>
              ))}
            </section>
          ))}
        </div>

        <p className="text-[11px] text-muted-foreground border-t pt-3 leading-relaxed">
          A nota vira o percentual da <strong>Produtividade</strong>: 80 ou mais recebe cheia ·
          70 recebe 75% · 60 recebe 50% · abaixo de 60, não recebe.
        </p>
      </DialogContent>
    </Dialog>
  );
}
